import * as React from 'react';
import { List, ListItem, ListItemIcon, Checkbox, ListItemText, InputLabel } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

import { Location } from 'types/location.type';
import LrDialog from 'components/LrDialog';
import NotesInput from 'components/LrNotes/components/NotesInput';
import { useRef } from 'react';
import { LrNotesInputAPI } from 'components/LrNotes';
import { LogCreateOptions } from 'types/logs.types';
import { useSelector } from 'react-redux';
import { getLogTypesByCode, getProductLineTypesByCode } from 'application/redux/selectors';
import { RestoreLocationsRequest } from 'types/underwriting.type';
import { AmType } from 'types/types.type';

interface Props {
    locations: Location[];
    noteLocations: Location[];
    closeDialogCallback: () => void;
    submissionId: string;
    submitRestoreRequest: (payload: RestoreLocationsRequest) => void;
    productLineTypeCode: string;
}

const getFormattedAddress = (location: Partial<Location>) => {
    const { address1, address2, city, state, zip, formattedAddress } = location;
    if (formattedAddress) {
        return formattedAddress;
    }
    return `${address1} ${address2}, ${city}, ${state} ${zip}`;
};

const RestoreUnavailableLocationDialog: React.FunctionComponent<Props> = (props) => {
    const {
        locations,
        noteLocations,
        closeDialogCallback,
        submissionId,
        submitRestoreRequest,
        productLineTypeCode,
    } = props;

    const logTypes = useSelector(getLogTypesByCode);
    const productLineTypes: Record<string, AmType> = useSelector(getProductLineTypesByCode);

    // Local State
    const [checkedLocations, setCheckedLocations] = React.useState(
        locations.reduce((map, l) => {
            map[l.id] = false;
            return map;
        }, {} as Record<number, boolean>)
    );
    const notesInputAPI = useRef<LrNotesInputAPI>(null);
    const [isValidRequest, setValidRequest] = React.useState(false);
    const [locationRestoreMessage, setLocationRestoreMessage] = React.useState('');
    const myType = productLineTypes[productLineTypeCode];

    const handleReasonChange = (reasonMessage: any) => {
        setLocationRestoreMessage(reasonMessage);
        setValidRequest(validateRestoreRequest(checkedLocations, reasonMessage));
    };

    const handleSubmitRemoval = () => {
        notesInputAPI.current && notesInputAPI.current.doSubmit();
    };

    const createLabel = (val: number) => `checkbox-list-label-${val}`;
    const handleToggle = (locId: number) => () => {
        const copy = { ...checkedLocations };
        copy[locId] = !copy[locId];
        handleChangeMade(copy);
    };

    const handleChangeMade = (checkArr: Record<number, boolean>) => {
        setCheckedLocations(checkArr);
        setValidRequest(validateRestoreRequest(checkArr, locationRestoreMessage));
    };

    const validateRestoreRequest = (checkArr: Record<number, boolean>, message: string): boolean => {
        return (
            Object.values(checkArr).indexOf(true) > -1 &&
            (Object.keys(checkArr).filter(
                (locId) => checkArr[Number(locId)] && noteLocations.some((nl) => nl.id === Number(locId))
            ).length === 0 ||
                (!!message && message.length > 10))
        );
    };

    const handleNoteSubmit = (result?: LogCreateOptions) => {
        if (isValidRequest) {
            const restoredLocationIds: number[] = [];
            Object.keys(checkedLocations).forEach((locId, index) => {
                if (checkedLocations[Number(locId)]) {
                    restoredLocationIds.push(Number(locId));
                }
            });
            submitRestoreRequest({
                locationIds: restoredLocationIds,
                restoreRequestMessage: result,
                productLineTypeCode: productLineTypeCode,
            });
        }
    };

    return (
        <LrDialog
            isOpen
            title={`Locations to Restore to Underwriting${myType?.displayValue ? ' for ' + myType.displayValue : ''}`}
            maxWidth="md"
            fullWidth
            onCancel={closeDialogCallback}
            cancelButtonText="Cancel"
            isSubmitEnabled={isValidRequest}
            submitButtonText="Submit"
            onSubmit={handleSubmitRemoval}
        >
            <List>
                {locations.map((value, index) => (
                    <ListItem key={value.id} onClick={handleToggle(value.id)} style={{ padding: 0 }}>
                        <ListItemIcon>
                            <Checkbox
                                checked={checkedLocations[value.id]}
                                style={{ marginBottom: 8 }}
                                tabIndex={-1}
                                inputProps={{ 'aria-labelledby': createLabel(value.id) }}
                            />
                        </ListItemIcon>
                        <ListItemText
                            style={{ padding: 0 }}
                            id={createLabel(value.id)}
                            primary={`${value.locationName}: ${getFormattedAddress(value)}`}
                        />
                    </ListItem>
                ))}
            </List>
            <Grid container spacing={1} direction="column">
                <Grid xs={12}>
                    <InputLabel htmlFor={'Location Restore Reason'}>{'Location Restore Reason'}</InputLabel>
                </Grid>
                <Grid xs={12} style={{ paddingTop: '1px' }}>
                    <NotesInput
                        options={{
                            submissionId: submissionId,
                            logTypeId: logTypes.LOCATION_RESTORE.id,
                        }}
                        doCreate={false}
                        hideSubmit={true}
                        onNoteChange={handleReasonChange}
                        onNoteSubmitted={handleNoteSubmit}
                        suppressSuccessMessage
                        flat
                        ref={notesInputAPI}
                        placeholder={
                            'Please indicate why requesting to restore location(s) to Underwriting for this product'
                        }
                    />
                </Grid>
            </Grid>
        </LrDialog>
    );
};

export default RestoreUnavailableLocationDialog;
